import React, {useEffect, useState} from 'react'
import NavBar from "../Components/NavBar";
import './home.css'
import {Button, Checkbox, FormControlLabel, IconButton, LinearProgress, Radio, RadioGroup, styled, TextField} from "@mui/material";
import useAxiosInstance from "../useAxiosInstance";
import FormData from "form-data";
import Swal from "sweetalert2";
import {RiAttachmentLine} from "react-icons/ri";
import formImage from "../assets/formImage.jpg";
import {CiTrash} from "react-icons/ci";

const regex = /^\d+\.\d+\.\d+$/;

// Package types as docs/update-sequencing.md names them. The server sends the enum names
// (Andriod sic, Anabee, AppApk); type 1 is the Android OS image, not a "main controller".
const TYPE_LABELS = {1: "Android OS", 2: "Sub controller firmware", 3: "App APK"};
const TYPE_NAMES = {Andriod: TYPE_LABELS[1], Anabee: TYPE_LABELS[2], AppApk: TYPE_LABELS[3]};

function Home() {
    const instance = useAxiosInstance();

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });
    const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
            confirmButton: "btn btn-success", cancelButton: "btn btn-danger"
        }, buttonsStyling: true
    });

    const [data, setData] = useState({file: "", type: 1, version: "", minCurrent: "", maxCurrent: "", qaOnly: true, bootloader: ""});
    const [response, setResponse] = useState([]);
    const [latestVersion, setLatestVersion] = useState({});
    const [IsSpinner, setIsSpinner] = useState(false);
    // Upload progress: null when idle, else {loaded, total, startedAt}
    const [progress, setProgress] = useState(null);

    const inputFileHandler = (event) => {
        const {name} = event.target;
        setData({
            ...data,
            [name]: event.target.files[0]
        });
    };

    const getHistory = () => {

        instance.get('FotaFiles/GetHistory')
            .then((response) => {
                setIsSpinner(false);
                setResponse(response);
            })
            .catch(() => {
                setIsSpinner(false);
            });
    };
    const getLatestVersion = () => {

        instance.get('FotaFiles/GetLatestVersion')
            .then((response) => {
                setIsSpinner(false);
                setLatestVersion(response);
            })
            .catch(() => {
                setIsSpinner(false);
            });
    };

    // Text for a popup's HTML: package names come from the uploader
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);
    const describe = (r) => `${esc(r.name)} ${esc(r.version)} (${esc(TYPE_NAMES[r.type] || r.type)})`;

    // QA signed it off: offer the package to everyone, not just the QA devices. The popup names the
    // package: the Release buttons sit next to each other and a release can't be undone here.
    const releaseFile = (r) => {
        swalWithBootstrapButtons.fire({
            html: `<div class="mb-4"><div class="swalHead">Release ${describe(r)} to everyone?</div>` +
                '<div class="fs-5 text-start">All units will be offered this update, not only the QA devices. ' +
                'This can\'t be undone from the portal.</div></div>',
            showCancelButton: true,
            confirmButtonText: 'Release',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setIsSpinner(true);
            instance.post(`FotaFiles/Release/${r.id}`)
                .then(() => {
                    Toast.fire({icon: "success", title: `Released ${r.name} ${r.version} to everyone`});
                    getHistory();
                })
                .catch(() => {
                })
                .finally(() => setIsSpinner(false));
        });
    };

    const deleteFile = (r) => {
        swalWithBootstrapButtons.fire({
            html: '<div class="d-flex gap-2 pt-1">' +
                '<div class="swalIcon">' +
                '<i class="fa-solid fa-2x fa-trash-can text-danger"></i>' +
                '</div>' +
                '<div class="mb-4"> ' +
                '<div class="swalHead">Delete!</div>' +
                `<div class="fs-5 text-start">Delete ${describe(r)} and its stored file? This can't be undone.</div>` +
                '</div>' +
                '</div>',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: "Cancel!"
        }).then((result) => {
            if (result.isConfirmed) {
                setIsSpinner(true);
                instance.delete(`FotaFiles/DeleteFile/${r.id}`).then((response) => {
                    Toast.fire({
                        icon: "success",
                        title: "Deleted Successfully"
                    });
                    getHistory();
                    getLatestVersion();
                })
                    .catch(() => {
                    }).finally(() => {
                    setIsSpinner(false);
                });
            }
        }).catch(() => {
        })
    }

    const uploadFotaFile = (data) => {
        setProgress({loaded: 0, total: 0, startedAt: Date.now()});
        instance.post('FotaFiles/UploadFotaFile', data, {
            headers: {'Content-Type': 'multipart/form-data'},
            onUploadProgress: (e) => setProgress(p => ({...p, loaded: e.loaded, total: e.total || p.total})),
        })
            .then((response) => {
                Toast.fire({
                    icon: "success",
                    title: "Uploaded Successfully"
                });
                setData({
                    file: "", type: 1, version: "", minCurrent: "", maxCurrent: "", qaOnly: true, bootloader: ""
                })
                getHistory();
                getLatestVersion();
            })
            .catch(() => {
            }).finally(() => {
            setIsSpinner(false);
            setProgress(null);
        });
    };

    const mb = (bytes) => (bytes / 1048576).toFixed(1);
    const progressText = () => {
        if (!progress) return "";
        const {loaded, total, startedAt} = progress;
        if (total && loaded >= total) {
            // The file is sent; the server is storing it and computing its SHA-256
            return `Sent ${mb(total)} MB. Saving on the server…`;
        }
        const seconds = (Date.now() - startedAt) / 1000;
        const rate = seconds > 0 ? loaded / seconds : 0;
        const eta = rate > 0 && total ? Math.round((total - loaded) / rate) : null;
        return `${mb(loaded)} of ${mb(total)} MB` +
            (rate > 0 ? ` · ${mb(rate)} MB/s` : "") +
            (eta !== null ? ` · about ${eta >= 60 ? `${Math.floor(eta / 60)} min ${eta % 60} s` : `${eta} s`} left` : "");
    };
    const percent = progress && progress.total ? Math.min(100, Math.round(progress.loaded * 100 / progress.total)) : 0;

    useEffect(() => {
        setIsSpinner(true);
        getHistory();
        getLatestVersion();
    }, []);

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)', height: 1,
        whiteSpace: 'nowrap', width: 1
    });

    // A validation popup must not return focus to Submit (keys typed right after closing it were
    // lost there): focus goes to the field that needs fixing instead.
    const warn = (text, field) => {
        Swal.fire({
            text,
            returnFocus: false,
            didClose: () => {
                const el = field && document.querySelector(`form.form [name="${field}"]`);
                if (el) el.focus();
            }
        });
        return false;
    };

    const submitHandler = (e) => {
        e.preventDefault();

        if (!data.version) return warn("Please enter the version.", "version");
        if (!data.file || !data.type) return warn("Please attach a file.");
        if (!regex.test(data.version)) {
            return warn("Please follow the correct version format, such as 1.0.0.", "version");
        }
        for (const [label, v, field] of [["Minimum", data.minCurrent, "minCurrent"], ["Maximum", data.maxCurrent, "maxCurrent"]]) {
            if (v && !regex.test(v)) {
                return warn(`${label} current version: use the format 1.0.0, or leave it empty.`, field);
            }
        }
        const expectedExtension = data.type === 3 ? ".apk" : ".zip";
        if (!data.file.name.toLowerCase().endsWith(expectedExtension)) {
            return warn(`Please attach a ${expectedExtension} file for this type.`);
        }

        const formData = new FormData();
        formData.append('File', data.file);
        formData.append('Type', data.type);
        formData.append('Version', data.version);
        if (data.minCurrent) formData.append('MinCurrentVersion', data.minCurrent);
        if (data.maxCurrent) formData.append('MaxCurrentVersion', data.maxCurrent);
        formData.append('QaOnly', data.qaOnly ? 'true' : 'false');
        // Controller only, "installs:N" / "requires:N": stage 1 installs bootloader 2, the v2 -> v3
        // package installs 3 (and needs "Only from" a boot-validated release); new-key releases need 2.
        const [blMode, blVersion] = data.bootloader.split(":");
        if (data.type === 2 && blMode === "installs" && blVersion === "3" && !data.minCurrent) {
            return warn("Bootloader 3 starts only a boot-validated release: set \"Only from\" (e.g. 1.6.7).", "minCurrent");
        }
        if (data.type === 2 && blMode === "installs") formData.append('InstallsBootloader', blVersion);
        if (data.type === 2 && blMode === "requires") formData.append('RequiresBootloader', blVersion);

        uploadFotaFile(formData);
        setIsSpinner(true);
    }

    return (
        <div className="innerMainContainer">
            <NavBar title="MI Health File Uploader." loading={IsSpinner}/>
            <div className="inputForm mb-3">
                <div className="imageContainer">
                    <img src={formImage} alt="form"/>
                </div>
                <form className="form" onSubmit={submitHandler}>
                    <h1 className="text-center mb-3" style={{letterSpacing: 1}}>Attach your file here.</h1>
                    <TextField
                        sx={{width: "100%", marginBottom: "1rem"}} size="small"
                        label="Version" error={!!(!regex.test(data.version) && data.version)}
                        name="version"
                        helperText={!regex.test(data.version) && data.version ? "Please follow the correct version format, such as 1.0.0" : ""}
                        value={data.version} placeholder="1.0.0"
                        onChange={(e) => setData(prevState => ({
                            ...prevState, version: e.target.value
                        }))}/>
                    <div className="d-flex gap-2">
                        <TextField
                            sx={{width: "100%", marginBottom: "1rem"}} size="small"
                            label="Only from version (optional)" name="minCurrent"
                            error={!!(data.minCurrent && !regex.test(data.minCurrent))}
                            helperText="Devices on this version or later"
                            value={data.minCurrent} placeholder="1.5.6"
                            onChange={(e) => setData(prevState => ({...prevState, minCurrent: e.target.value}))}/>
                        <TextField
                            sx={{width: "100%", marginBottom: "1rem"}} size="small"
                            label="Only up to version (optional)" name="maxCurrent"
                            error={!!(data.maxCurrent && !regex.test(data.maxCurrent))}
                            helperText="Devices on this version or earlier"
                            value={data.maxCurrent} placeholder="1.5.5"
                            onChange={(e) => setData(prevState => ({...prevState, maxCurrent: e.target.value}))}/>
                    </div>
                    <FormControlLabel
                        sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}}
                        control={<Checkbox checked={data.qaOnly}
                                           onChange={(e) => setData(prev => ({...prev, qaOnly: e.target.checked}))}/>}
                        label="QA devices only (release to everyone after QA)"/>
                    <Button component="label" variant="outlined" className="w-100 justify-content-start mb-2"
                            style={{padding: "10px"}}>
                        <RiAttachmentLine/>
                        <VisuallyHiddenInput onChange={inputFileHandler} name="file" type="file"
                                             accept={data.type === 3 ? ".apk" : ".zip"}/>
                        <small style={{fontWeight: 380}} className="ms-2">{data.file.name}</small>
                    </Button>
                    <RadioGroup className="mb-1 ms-1"
                                value={data.type}
                                onChange={(event) => setData(prev => ({
                                    ...prev,
                                    type: parseInt(event.target.value, 10)
                                }))} row>
                        <FormControlLabel
                            sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}}
                            value={1} checked={data.type === 1}
                            control={<Radio/>}
                            label={TYPE_LABELS[1]}/>
                        <FormControlLabel
                            sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}}
                            value={2} checked={data.type === 2}
                            control={<Radio/>}
                            label={TYPE_LABELS[2]}/>
                        <FormControlLabel
                            sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}}
                            value={3} checked={data.type === 3}
                            control={<Radio/>}
                            label={TYPE_LABELS[3]}/>
                    </RadioGroup>
                    {data.type === 2 && <RadioGroup className="mb-1 ms-1" value={data.bootloader}
                                                    onChange={(e) => setData(prev => ({...prev, bootloader: e.target.value}))}>
                        <FormControlLabel sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}} value=""
                                          control={<Radio size="small"/>} label="Any bootloader (old-key package)"/>
                        <FormControlLabel sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}} value="installs:2"
                                          control={<Radio size="small"/>} label="Stage 1: installs bootloader 2 (rotation package)"/>
                        <FormControlLabel sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}} value="installs:3"
                                          control={<Radio size="small"/>} label="Installs bootloader 3 (boot validation; set Only from, e.g. 1.6.7)"/>
                        <FormControlLabel sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}} value="requires:2"
                                          control={<Radio size="small"/>} label="Needs bootloader 2 (signed with the new key)"/>
                        <FormControlLabel sx={{"& .MuiFormControlLabel-label": {fontSize: 14}}} value="requires:3"
                                          control={<Radio size="small"/>} label="Needs bootloader 3"/>
                    </RadioGroup>}
                    <Button className="w-100" variant="contained" type="submit" disabled={!!progress}>
                        {progress ? `Uploading… ${percent}%` : "Submit"}
                    </Button>
                    {progress && <div className="mt-2">
                        <LinearProgress variant={percent >= 100 ? "indeterminate" : "determinate"} value={percent}/>
                        <small className="text-muted">{progressText()}</small>
                    </div>}
                    <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-3">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            {TYPE_LABELS[1]} latest version <span
                            className={latestVersion.andriodLatestVersion ? "border-bottom border-black" : ""}>{latestVersion.andriodLatestVersion ? latestVersion.andriodLatestVersion : "--.--.--"}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            {TYPE_LABELS[2]} latest version <span
                            className={latestVersion.annabLatestVersion ? "border-bottom border-black" : ""}>{latestVersion.annabLatestVersion ? latestVersion.annabLatestVersion : "--.--.--"} </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            {TYPE_LABELS[3]} latest version <span
                            className={latestVersion.appApkLatestVersion ? "border-bottom border-black" : ""}>{latestVersion.appApkLatestVersion ? latestVersion.appApkLatestVersion : "--.--.--"}</span>
                        </div>
                    </div>
                </form>
            </div>
            <div className="table-container">
                <table>
                    <thead>
                    <tr>
                        <th>Id</th>
                        <th>Name</th>
                        <th>Link</th>
                        <th>Version</th>
                        <th>For devices on</th>
                        <th>Channel</th>
                        <th>Type</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {response.map(r => (
                        <tr key={r.id}>
                            <td>{r.id}</td>
                            <td>{r.name}</td>
                            <td>
                                <a rel="noreferrer" target="_blank"
                                   href={r.link}>{r.link}
                                </a>
                            </td>
                            <td>{r.version}</td>
                            <td>{r.minCurrentVersion || r.maxCurrentVersion
                                ? `${r.minCurrentVersion || "any"} – ${r.maxCurrentVersion || "any"}` : "any"}
                                {r.installsBootloader ? ` · installs bootloader ${r.installsBootloader}` : ""}
                                {r.requiresBootloader ? ` · needs bootloader ${r.requiresBootloader}` : ""}</td>
                            <td>{r.qaOnly
                                ? <span>QA only <Button size="small" variant="outlined" className="ms-2"
                                                        onClick={() => releaseFile(r)}>Release</Button></span>
                                : "Everyone"}</td>
                            <td>{TYPE_NAMES[r.type] || r.type}</td>
                            <td>
                                <IconButton aria-label={`Delete ${r.name} ${r.version}`} title="Delete"
                                            onClick={() => deleteFile(r)}>
                                    <CiTrash size={22} aria-hidden="true"/>
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default Home
